import { Box, Stack, Typography, useTheme } from '@mui/material';
import React, { FC } from 'react';
import {
  Cancel,
  CardMembership,
  CheckCircle,
  ErrorOutline,
  HelpOutline,
  QuestionMark,
  Schedule,
} from '@mui/icons-material';
import { transformBookingCounts, type BookingStatusItem } from '../../../utils/bookingStatusUtils';

interface ReportStatusStripeProps {
  data: BookingStatusItem[];
}

const ReportStatusStripe: FC<ReportStatusStripeProps> = ({ data }) => {
  const theme = useTheme();
  const transformed = transformBookingCounts(data || []);

  const getIconComponent = (icon: string) => {
    switch (icon) {
      case 'fa fa-check-circle text-success':
        return CheckCircle;
      case 'fa fa-exclamation-circle text-info':
        return ErrorOutline;
      case 'fa fa-clock-o text-warning':
        return Schedule;
      case 'fa fa-times-circle-o text-danger':
        return Cancel;
      case 'fa fa-check-circle-o text-secondary':
        return HelpOutline;
      case 'fa fa-check-circle-o text-muted':
        return CardMembership;
      case 'fa fa-question-circle text-secondary':
        return QuestionMark;
      default:
        return CheckCircle;
    }
  };

  const getIconColor = (icon: string) => {
    if (icon.includes('text-success')) return '#66BB6A';
    if (icon.includes('text-info')) return '#42A5F5';
    if (icon.includes('text-warning')) return '#FFC107';
    if (icon.includes('text-danger')) return '#E57373';
    if (icon.includes('text-secondary')) return '#FFA726';
    if (icon.includes('text-muted')) return '#4DB6AC';
    return theme.palette.primary.main;
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: theme.palette.common.white,
        borderRadius: 2,
        px: 1.25,
        py: 1,
      }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {transformed.map((item, idx) => {
          const Icon = getIconComponent(item.icon);
          const color = getIconColor(item.icon);
          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                py: 0.75,
                px: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: `${color}55`,
                backgroundColor: `${color}10`,
              }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.common.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 0 0 1px ${color}33`,
                  color,
                }}>
                <Icon sx={{ fontSize: 14 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color, lineHeight: 1 }}>{item.value}</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                }}>
                {item.name}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ReportStatusStripe;
